'use client';

import { motion } from 'framer-motion';
import { PortableText } from '@portabletext/react';

import { CareerListItem, PageCareerDetailSchema } from '@/sanity/pages/PageCareerDetailTemplate';
import { richTextComponent } from '@/sanity/sanityPortableText';
import { CTAButton } from '@/shadcn/ui/button';

const ease = [0.25, 0.1, 0.25, 1] as const;

function CheckIcon({ variant }: { variant: 'onDark' | 'onLight' }) {
  const box = variant === 'onDark' ? '#D6EEE7' : '#228E71';
  const stroke = variant === 'onDark' ? '#0D2922' : '#FFFFFF';

  return (
    <div
      className="flex h-[4rem] w-[4rem] shrink-0 items-center justify-center"
      style={{ backgroundColor: box }}
    >
      <svg viewBox="0 0 32 32" fill="none" className="h-[4rem] w-[4rem]" aria-hidden="true">
        <path d="M10 16.8322L13.4229 20L22 12" stroke={stroke} strokeWidth="2" />
      </svg>
    </div>
  );
}

function ItemList({
  items,
  variant,
}: {
  items?: CareerListItem[];
  variant: 'onDark' | 'onLight';
}) {
  if (!items?.length) return null;

  return (
    <ul className="mb-0 flex list-none flex-col p-0">
      {items.map((item, index) => (
        <li key={item._key ?? index} className="flex flex-col">
          <div className="flex items-center gap-[1.375rem] py-[2.25rem]">
            <CheckIcon variant={variant} />
            <span
              className={`text-[2rem] font-medium leading-[1.3] ${
                variant === 'onDark' ? 'text-white/[0.62]' : 'text-black/[0.62]'
              }`}
            >
              {item.text}
            </span>
          </div>
          {index < items.length - 1 && (
            <div className={`h-px ${variant === 'onDark' ? 'bg-white/10' : 'bg-black/10'}`} />
          )}
        </li>
      ))}
    </ul>
  );
}

export default function TasksProfileSection({ data }: { data: PageCareerDetailSchema }) {
  const hasTasks = (data.tasksItems?.length ?? 0) > 0;
  const hasProfile = (data.profileItems?.length ?? 0) > 0;

  if (!hasTasks && !hasProfile) return null;

  return (
    <section className="w-full bg-white px-[2rem] pb-[10rem] lg:px-[6rem] lg:pb-[12.5rem]">
      <div className="mx-auto flex w-[min(124rem,100%)] flex-col items-center">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.2 }}
          transition={{ duration: 0.8, ease }}
          className="flex w-full flex-col gap-[2rem] lg:flex-row lg:gap-[4rem]"
        >
          {/* Tasks — dark card */}
          {hasTasks && (
            <div className="relative flex-1 overflow-hidden bg-[#0d2922] max-lg:p-[4rem] lg:px-[6.875rem] lg:py-[6rem]">
              <div
                aria-hidden="true"
                className="pointer-events-none absolute inset-0 max-lg:[mask-image:radial-gradient(47.125rem_106.375rem_at_47.8rem_1.84rem,#000,transparent)] lg:[mask-image:radial-gradient(81.25rem_106.375rem_at_82.4rem_1.775rem,#000,transparent)]"
              >
                <div className="absolute top-0 w-[56.75rem] origin-top-left rotate-90 bg-[url('/new-pricing/platform-dialog/bg-texture.webp')] bg-[length:56.75rem_100.855rem] bg-center opacity-30 max-lg:left-[49.883rem] max-lg:h-[58.513rem] lg:left-[86.013rem] lg:h-[100.855rem]" />
              </div>
              <div className="relative z-10 flex flex-col">
                {data.tasksLabel && (
                  <span className="pb-[1.75rem] font-geist text-[2.25rem] font-semibold uppercase leading-[1.33] text-[#d6eee7]">
                    {data.tasksLabel}
                  </span>
                )}
                <ItemList items={data.tasksItems} variant="onDark" />
              </div>
            </div>
          )}

          {/* Profile — light card */}
          {hasProfile && (
            <div className="flex flex-1 flex-col border border-black/10 bg-white max-lg:p-[4rem] lg:px-[6.875rem] lg:py-[6rem]">
              {data.profileLabel && (
                <span className="pb-[1.75rem] font-geist text-[2.25rem] font-semibold uppercase leading-[1.33] text-black">
                  {data.profileLabel}
                </span>
              )}
              <ItemList items={data.profileItems} variant="onLight" />
            </div>
          )}
        </motion.div>

        {data.contentText && (
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.3 }}
            transition={{ duration: 0.8, ease, delay: 0.1 }}
            className="richtext mt-[4rem] w-full max-w-[86.5rem] prose-p:mb-0 prose-p:text-[2rem] prose-p:leading-[1.5] lg:mt-[4rem] px-[0.2rem] lg:prose-p:text-[2.25rem]"
          >
            <PortableText value={data.contentText} components={richTextComponent} />
          </motion.div>
        )}

        {data.contentCTA?.cta && (
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.3 }}
            transition={{ duration: 0.8, ease, delay: 0.2 }}
            className="mt-[3rem] w-full lg:mt-[4rem] lg:w-fit lg:min-w-[29.75rem] lg:[&_.button]:min-h-[5.75rem]"
          >
            <CTAButton data={data.contentCTA} />
          </motion.div>
        )}
      </div>
    </section>
  );
}
